import { SPICE, TOUCHSTONE } from "../../../../constants/libraryConstants";
import ColorGenerator, { COLORTYPES } from "../../../helper/colorGenerator";
import { getMaxTextWidth } from "../../../helper/getTextWidth";
import { getPkgSpiceModelList, getTouchstoneParse } from "../../library";
import libraryConstructor from "../../library/libraryConstructor";
import { getCompNames } from "../channelDisplayHelper";
import { getSignalElementType } from "../signalsIdentification";
import { netToPinType, channelModelTypes, colorPalette } from "./constant";

/**
 * 
 * @param {Object} currentComp 
 * @param {Array} otherComps 
 * @param {Array} signals 
 * @param {string} serdesType 
 * @returns {{txModelInfoList, signalPinList, rxModelInfoList, modelTitleList, maxSignalWidth}}
 */
async function getDisplayModelInfo(currentComp, otherComps, signals, serdesType, fileColors) {
  const pins = currentComp.pins || [];
  const signalNames = pins.map(pin => pin.signal);
  const usableSignals = signals.filter(signal => signalNames.includes(signal.name));
  const maxSignalWidth = getMaxTextWidth(usableSignals.map(signal => signal.name), 12, 400, 500)

  const txPins = [], rxPins = [], txComps = [];
  function getPins(nets = [], signalName, pinType) {
    // pin_in
    let pin_in = { signal: signalName };
    for (const comp of otherComps) {
      const pins = comp.pins || [];
      const findPin = pins.find(pin => pin.signal === signalName && nets.includes(pin.net));

      if (findPin) {
        pin_in = { ...findPin, compName: comp.name };
        if (!txComps.find(it => it.name === comp.name)) {
          txComps.push(comp)
        }
      }
    }

    // pin_out
    const pin_out = pins.find(pin => pin.signal === signalName && nets.includes(pin.net)) || {};
    txPins.push({ ...pin_in, pinType });
    rxPins.push({ ...pin_out, pinType, compName: currentComp.name });

    return { pin_in, pin_out };
  }

  // {signalName, pins: [{pins_in, pins_out}]}
  const signalPinList = usableSignals.map(signal => {
    const pins = [];
    for (const netType of getSignalElementType(serdesType)) {
      const { pin_in, pin_out } = getPins(signal[netType], signal.name, netToPinType[netType]);
      pins.push({ pin_in, pin_out });
    }

    return pins;
  }).flat();

  // [{modelType, type, pairs, files}]
  const txModelList = [];
  const txPinNames = txPins.map(it => [it.pin, `${it.pin}_u`, `${it.pin}_pcb`]).flat();
  for (const comp of txComps) {
    const modelList = comp.modelList || [];
    for (let i = 0; i < modelList.length; i++) {
      const { pairs, files } = getIncludePairsAndFiles(modelList[i], comp.name, txPinNames);
      // const { modelType, topology } = modelList[i];
      // const includeTermination = modelType === DIE && topology === SERIES;
      // let terminationPairs = null, terminationFiles = null;
      // if (includeTermination) {
      //   const terminationData = getIncludePairsAndFiles(modelList[i].termination, comp.name, txPinNames);
      //   terminationPairs = terminationData.pairs;
      //   terminationFiles = terminationData.files;
      // }
      if (txModelList[i]) {
        txModelList[i].pairs.push(...pairs);
        txModelList[i].files.push(...files);
        // if (includeTermination) {
        //   txModelList[i].termination.pairs.push(...terminationPairs);
        //   txModelList[i].termination.files.push(...terminationFiles);
        // }
      } else {
        // const otherData = includeTermination ? { termination: { ...modelList[i].termination, pairs: terminationPairs, files: terminationFiles } } : {};
        // txModelList[i] = { ...modelList[i], files, ...otherData };
        txModelList[i] = { ...modelList[i], pairs, files };
      }
    }
  }

  const rxModelList = (currentComp.modelList || []).filter(model => model.type !== "None");
  const defaultModelTitleWidth = maxSignalWidth + 210;
  const { modelInfoList: txModelInfoList, modelTitleList: txModelTitleList, resFileColors: txFileColors } = getSpecifyFormatModelList(txModelList, txPins, 'tx', defaultModelTitleWidth, fileColors),
    { modelInfoList: rxModelInfoList, modelTitleList: rxModelTitleList, resFileColors: rxFileColors } = getSpecifyFormatModelList(rxModelList, rxPins, 'rx', defaultModelTitleWidth, fileColors);

  const getFiles = (modelList) => {
    return modelList.map(it => {
      // if (it.termination && Array.isArray(it.termination.files)) {
      //   return [...it.termination.files, ...it.files];
      // }
      return it.files;
    }).flat();
  }
  const txFiles = getFiles(txModelList);
  const rxFiles = getFiles(rxModelList);
  // This ensures each libraryId appears only once in the final result.
  const fileInfoList = await getFileParse(Array.from(new Map([...txFiles, ...rxFiles].map(item => [item.libraryId, item])).values()));

  return {
    txModelInfoList,
    signalPinList,
    rxModelInfoList,
    modelTitleList: [...txModelTitleList, { title: "PCB", type: "main-layout" }, ...rxModelTitleList],
    maxSignalWidth,
    fileInfoList,
    txComps,
    resFileColors: { ...txFileColors, ...rxFileColors }
  }
}

function getSpecifyFormatModelList(modelList, pins, type, defaultModelTitleWidth, fileColors) {
  const otherPairInfo = (item, fileName, type) => ({
    signal: item.signal,
    // pinDisplay: type === 'out' ? modelType === DIE ? `${item.pin}_die` : `${item.pin}_out` : item.pin,
    pinDisplay: type === 'out' ? `${item.pin}_out` : item.pin,
    pinType: item.pinType,
    comp: item.compName,
    net: item.net,
    fileName
  })
  // console.log(modelList, pins);
  const modelInfoList = [], modelTitleList = [];
  let resFileColors = {};
  for (const modelIndex in modelList) {
    const { pairs, modelType, files } = modelList[modelIndex];
    // let otherModelTitleWidth = 0, otherModelType = "";
    // if (modelType === DIE) {
    //   if (topology === SERIES) {
    //     otherModelType = "Termination"
    //     otherModelTitleWidth = termination.type === SPICE ? 100 : 170
    //   } else {
    //     otherModelTitleWidth = -92;
    //   }
    // }

    const { colors, sortedFiles } = generateFilesColor(pairs, files, type, modelIndex, fileColors,);
    resFileColors = { ...resFileColors, ...colors };
    const modelTitleInfo = {
      modelType,
      type,
      modelIndex,
      width: defaultModelTitleWidth,
      files: sortedFiles
      // width: defaultModelTitleWidth + otherModelTitleWidth,
      // otherModelType,
      // otherModelTitleWidth
    }
    if (type === 'tx') {
      modelTitleList.unshift(modelTitleInfo)
    } else {
      modelTitleList.push(modelTitleInfo)
    }
    let fileName = "";
    const resPairs = pins.map((item = {}) => {
      const pin = pairs.find(it => it.pin === item.pin) || {};
      const pin_u = pairs.find(it => it.pin === `${item.pin}_u` || it.pin === `${item.pin}_pcb`) || {};

      const pinFileName = (files.find(it => it.libraryId === pin.libraryId) || {}).fileName;
      const pinUFileName = (files.find(it => it.libraryId === pin_u.libraryId) || {}).fileName;
      fileName = pinFileName || pinUFileName || fileName;

      // let pair_t = null;
      // if (modelType === DIE) {
      //   const pairInfo = (termination.pairs || []).find(it => it.pin === item.pin) || {};
      //   const fileName_t = (termination.files.find(it => it.libraryId === pairInfo.libraryId) || {}).fileName;
      //   pair_t = {
      //     ...pairInfo,
      //     ...otherPairInfo(item, fileName_t, '', modelType)
      //   }
      // }

      return {
        pair_in: {
          ...pin,
          ...otherPairInfo(item, fileName, '', modelType),
        },
        pair_out: {
          ...pin_u,
          ...otherPairInfo(item, fileName, "out", modelType),
        },
        // ...(pair_t ? { pair_t } : {}),
      };
    });
    if (type === 'tx') {
      modelInfoList.unshift({ ...modelList[modelIndex], pairs: resPairs, files: sortedFiles, modelIndex });
    } else {
      modelInfoList.push({ ...modelList[modelIndex], pairs: resPairs, files: sortedFiles, modelIndex });
    }
  }

  return { modelInfoList, modelTitleList, resFileColors };
}

function getIncludePairsAndFiles(model, comp, txPinNames) {
  const pairs = model.pairs.filter(it => txPinNames.includes(it.pin));
  // const modelKeys = pairs.map(it => it.modelKey);
  const files = model.files
    // .filter(it => modelKeys.includes(it.modelKey))
    .map(f => {
      return { ...f, comp }
    })

  return { pairs, files };
}

function getNetText(net_in, net_out) {
  if (net_in === net_out) {
    return net_in;
  } else {
    return [net_in || "", net_out || ""].filter(it => !!it).join(", ");
  }
}

/**
 * get ports, models(subckt, ports)
 * @param {Array} files [{libraryId, fileName, type}]
 * @returns 
 */
async function getFileParse(files) {
  if (!files || files.length === 0) {
    return;
  }
  //filter libraryId not exist files
  const _files = files.filter(item => !!item.libraryId);
  const fileInfoList = {};
  //get files ports
  const res = await getFilesParseList(_files);

  if (res && Array.isArray(res)) {
    for (const file of _files) {
      const libraryId = file.libraryId;
      let _ports = [];
      const currentFileInfo = res.find(it => it && it.libraryId === libraryId);
      if (file.type === TOUCHSTONE) { // currentFileInfo: {libraryId, ports}
        _ports = currentFileInfo && Array.isArray(currentFileInfo.ports) ? currentFileInfo.ports : [];
        //update file portsObj
        fileInfoList[libraryId] = { libraryId, fileName: file.fileName, type: TOUCHSTONE, ports: _ports };
      } else if (file.type.toLowerCase() === SPICE) { // currentFileInfo: {libraryId, models: [{name, ports}]}
        const models = currentFileInfo && Array.isArray(currentFileInfo.models) ? [...currentFileInfo.models] : [];
        fileInfoList[libraryId] = { libraryId, fileName: file.fileName, type: SPICE, models };
      }
    }
  }

  return fileInfoList;
}

function getFilesParseList(files = []) {
  const promiseList = files.map(item => {
    if (item.type === TOUCHSTONE) {
      return getTouchstoneParse(item.libraryId, item.fileName);
    } else if (item.type.toLowerCase() === SPICE) {
      return getPkgSpiceModelList(item.libraryId);
    }
    return null;
  }).filter(item => !!item);
  return Promise.all(promiseList);
}

/**
 * 
 * @param {Object} pairInfo {modelKey, port, pin, net, signal, libraryId, subckt} 
 * @param {Object} selectFileInfo {modelKey, fileName, libraryId, subckt}
 * @param {Array} pairs [{pair_in,pair_out}]
 * @param {Array} fileInfoList [{libraryId, fileName, type, ports | models}]
 * @param {Array} files [{libraryId, fileName, type, modelKey, subckt}]
 * @returns {{portList, fileList, fileValue}}
 * @returns 
 */
function getPortListAndFileList(pairInfo, selectFileInfo, pairs = [], files = [], fileInfoList = {}, otherKeyStr) {
  if (!fileInfoList || Object.keys(fileInfoList).length === 0) {
    return { portList: [], fileList: [], fileValue: "" };
  }

  let { libraryId = "", modelKey = "", subckt = "" } = pairInfo || selectFileInfo, fileName = (selectFileInfo || {}).fileName;
  if (!libraryId || !modelKey) {
    const currFile = files[0] || {};
    libraryId = currFile.libraryId;
    modelKey = currFile.modelKey;
    subckt = currFile.subckt;
    fileName = currFile.fileName;
  } else if (!fileName) {
    const find = files.find(item => item.libraryId === libraryId && item.modelKey === modelKey);
    fileName = find ? find.fileName : "";
  }

  //find current libraryId ports
  const fileInfoObj = fileInfoList[libraryId] || null;
  if (!libraryId || !fileInfoObj || !fileInfoObj.ports || !fileInfoObj.ports.length) {
    return { portList: [], fileList: [], fileValue: "" };
  }

  // Obtain the selected port data of the pairs with the same modelKey
  const pairsArr = pairs.map(item => {
    const { pair_in, pair_out } = item;
    return pair_in || pair_out ? [pair_in, pair_out] : item;
  }).flat();
  // [library::modelKey] => ports
  const modelKeyPorts = {};
  for (const pair of pairsArr) {
    if (pair.libraryId && pair.modelKey && pair.node) {
      const key = `${pair.libraryId}::${pair.modelKey}`;
      if (modelKeyPorts[key]) {
        modelKeyPorts[key].push(pair.node);
      } else {
        modelKeyPorts[key] = [pair.node];
      }
    }
  }
  let usablePorts = modelKeyPorts[`${libraryId}::${modelKey}`] || [];
  if (fileInfoObj.ports.length === usablePorts.length) {
    // Find the Ports with the same libraryId but different ModelKey.
    const keys = Object.keys(modelKeyPorts);
    const targetKey = keys.find(key => modelKeyPorts[key].length < fileInfoObj.ports.length);
    if (targetKey) {
      usablePorts = modelKeyPorts[targetKey];
      modelKey = targetKey.split("::")[1];
    }
  }
  let ports = [];
  if (fileInfoObj.type === TOUCHSTONE) {
    // {port,netName, info}
    ports = (fileInfoObj.ports || []).map(item => ({
      value: item.port,
      disabled: usablePorts.includes(item.port),
      label: `${item.port}  ${item.info}`,
      title: `${item.port}  ${item.info}`
    }));
  } else if (fileInfoObj.type.toLowerCase() === SPICE && (fileInfoObj.models || []).length > 0) {
    const current = fileInfoObj.models.find(item => item.name === subckt);
    ports = (current ? current.ports || [] : []).map(port => ({
      value: port,
      // disabled: otherKeyStr === DIE_TERMINATION ? false : usablePorts.includes(port),
      disabled: usablePorts.includes(port),
      label: port,
      title: port
    }))
  }

  // Sort the ports, with the "disabled" data listed at the end.
  const portList = ports.sort((a, b) => {
    if (a.disabled && !b.disabled) {
      return 1;
    } else if (!a.disabled && b.disabled) {
      return -1;
    } else {
      return 0;
    }
  });

  const fileList = [], fileKeys = [];
  for (const file of files) {
    if (fileKeys.includes(`${file.libraryId}::${file.subckt}`)) {
      continue;
    }
    fileList.push({
      value: file.subckt ? `${file.libraryId}::${file.modelKey}::${file.subckt}` : `${file.libraryId}::${file.modelKey}`,
      label: file.subckt ? `${file.fileName}::${file.subckt}` : file.fileName || "",
      title: file.subckt ? `${file.fileName}::${file.subckt}` : file.fileName || "",
      className: 'spice-nodes-content-file'
    })
    fileKeys.push(`${file.libraryId}::${file.subckt}`);
  }

  return {
    portList,
    fileList,
    fileValue: {
      libraryId,
      modelKey,
      subckt,
      fileName
    }
  };
}

function getDisplayCompName(currentComp, txComps) {
  const rxCompName = getCompNames(currentComp, [currentComp], "string");
  const txCompNames = txComps.map(comp => {
    return getCompNames(comp, [comp], "string");
  }).join(", ");

  return {
    txCompName: txCompNames,
    rxCompName
  }
}

function getNextPairInfo(pairInfo, pairIndex, type, pairs, otherKeyStr) {
  const { pair_in = {}, pair_out = {} } = pairs[pairIndex];

  // 1. Determine whether the other side of the current pair has selected the node
  const otherPair = pair_in.pin === pairInfo.pin ? pair_out : pair_in;
  // if (!otherPair.node && otherKeyStr !== DIE_TERMINATION) {
  if (!otherPair.node) {
    return otherPair;
  }
  let flagIndex = pairIndex + 1;
  // 2. Go down and jump. 
  while (flagIndex % pairs.length !== pairIndex) {
    const index = flagIndex % pairs.length;
    const { pair_in, pair_out } = pairs[index];
    // if (otherKeyStr === DIE_TERMINATION) {// 如果DIE-TERMINATION
    //   if (!pairs[index].node) {
    //     return pairs[index];
    //   }
    // } else 
    if (type === "tx") {// If it is tx, it first checks out and then checks in.
      if (!pair_out.node) {
        return pair_out;
      } else if (!pair_in.node) {
        return pair_in;
      }
    } else { // Rx first checks "in", then checks "out".
      if (!pair_in.node) {
        return pair_in;
      } else if (!pair_out.node) {
        return pair_out;
      }
    }
    flagIndex++;
  }

  return null;
}

/**
 * @param {Array} files
 * @returns {Object} {libraryId: {libraryId, fileName, type, number, isExist}} 
 */
function getDisplayModelFiles(comps, files, modelType, type) {
  if (!Array.isArray(files)) {
    return [];
  }

  const allLibraryFiles = libraryConstructor.getLibraryValueList(channelModelTypes);
  const compToFiles = {}; // comp -> files
  for (const comp of comps) {
    const compFiles = type === 'rx' ? files : files.filter(it => it.comp === comp.name);
    // const filesMap = compFiles.reduce((countMap, file) => {
    //   if (file && file.libraryId) {
    //     const id = file.libraryId + "::" + (file.subckt || "");
    //     countMap[id] = countMap[id] ? { ...countMap[id], number: countMap[id].number + 1 } : { ...file, number: 1 };
    //   }
    //   return countMap;
    // }, {});

    // const resFiles = [];
    // for (const key in filesMap) {
    //   const { libraryId } = filesMap[key];
    //   const isExist = libraryConstructor.checkFile(types, libraryId);
    //   resFiles.push({ ...filesMap[key], isExist });
    // }

    compToFiles[comp.name] = compFiles.map(item => {
      const isExist = allLibraryFiles.find(it => it.id === item.libraryId);
      return { ...item, isExist }
    });
  }

  return compToFiles;
}

function getPortsObjAndSubcktList(files, pairs, portsObj, subcktList, isGetSubcktList, setData) {
  let _portsObj = { ...portsObj }, _subcktList = [...subcktList];
  return getFilesParseList(files).then(res => {
    if (res && Array.isArray(res)) {
      for (const file of files) {
        const libraryId = file.libraryId;
        const currentFileInfo = res.find(it => it && it.libraryId === libraryId);
        if (file.type === TOUCHSTONE) {
          //update file portsObj
          const ports = currentFileInfo && Array.isArray(currentFileInfo.ports) ? currentFileInfo.ports : [];
          _portsObj[libraryId] = [{ fileName: file.fileName, type: TOUCHSTONE, ports: ports }];
        } else if (file.type.toLowerCase() === SPICE) {
          const models = currentFileInfo && Array.isArray(currentFileInfo.models) ? [...currentFileInfo.models] : [];
          //update state subckt list
          const findIndex = _subcktList.findIndex(it => it.libraryId === libraryId);
          if (findIndex > -1) {
            _subcktList[findIndex] = {
              libraryId,
              fileName: file.fileName,
              models
            }
          } else {
            _subcktList.push({
              libraryId,
              fileName: file.fileName,
              models
            });
          }

          if (file.subckt) {
            const ports = [];
            const currentSubckt = models.find(item => item.name === file.subckt);
            const selectedPairs = pairs.filter(item => item.node && item.libraryId === libraryId && item.subckt === file.subckt);
            const selectedPorts = selectedPairs.map(item => item.node);
            //update ports
            if (currentSubckt && Array.isArray(currentSubckt.ports)) {
              currentSubckt.ports.forEach(item => {
                ports.push({
                  port: item,
                  info: item,
                  subckt: file.subckt,
                  select: selectedPorts.includes(item) ? true : false
                })
              });
            }

            //update ports
            if (_portsObj[libraryId]) {
              //find current subckt ports
              const index = _portsObj[libraryId].findIndex(item => item.subckt === file.subckt);
              if (index > -1) {
                //update current subckt ports
                _portsObj[libraryId][index] = { fileName: file.fileName, type: SPICE, subckt: file.subckt, ports };
              } else {
                //Add current subckt ports
                _portsObj[libraryId] = [..._portsObj[libraryId], { fileName: file.fileName, type: SPICE, subckt: file.subckt, ports }];
              }
            } else {
              //Add current file and current subckt ports
              _portsObj[libraryId] = [{ fileName: file.fileName, type: SPICE, subckt: file.subckt, ports }];
            }
          }
        }
      }

      setData(_portsObj, _subcktList);

      if (isGetSubcktList) return _subcktList;
    }
  })
}

function getTargetModelFiles(compName, modelIndex, otherKeyStr, components) {
  const comp = components.find(comp => comp.name === compName);
  if (!comp) return [];
  try {
    // return otherKeyStr === DIE_TERMINATION ? comp.modelList[modelIndex].termination.files : comp.modelList[modelIndex].files;
    return comp.modelList[modelIndex].files;
  } catch (error) {
    console.error(error);
    return [];
  }
}

function generateFilesColor(pairs = [], files = [], type, modelIndex, fileColors) {
  if (!pairs || pairs.length === 0 || !files || files.length === 0) {
    return { colors: {}, sortedFiles: [] };
  }
  const adjacentModelKeys = {};
  for (let i = 0; i < pairs.length; i++) {

    const currentModelKey = pairs[i].modelKey;
    if (!currentModelKey) {
      continue;
    }
    const lastIndex = i - 1;
    const nextIndex = i + 1;
    const adjacent = adjacentModelKeys[currentModelKey] || [];
    if (pairs[lastIndex] && pairs[lastIndex].modelKey !== currentModelKey) {
      adjacent.push(pairs[lastIndex].modelKey)
    }

    if (pairs[nextIndex] && pairs[nextIndex].modelKey !== currentModelKey) {
      adjacent.push(pairs[nextIndex].modelKey)
    }

    adjacentModelKeys[currentModelKey] = [...new Set(adjacent)]
  }

  const assignment = {};
  for (const modelKey in adjacentModelKeys) {
    const currentPos = `${type}-${modelIndex}-${modelKey}`;

    let colorInfo = fileColors[`${type}-${modelIndex}-${modelKey}`];
    if (colorInfo) {
      assignment[currentPos] = colorInfo;
      continue;
    }

    const constraints = new Set();
    const colorTypes = new Set([...COLORTYPES]);
    const modelKeys = adjacentModelKeys[modelKey];
    for (const key of modelKeys) {
      const pos = `${type}-${modelIndex}-${key}`;
      if (assignment[pos]) {
        const colorType = ColorGenerator.getColorHueRange(assignment[pos].bgColor);
        colorTypes.delete(colorType);
        constraints.add(assignment[pos]);
      }
    }

    const availableColors = Array.from(colorTypes).map(item => colorPalette[item] || []).flat().filter(color => !constraints.has(color));
    if (availableColors.length === 0) {
      throw new Error(`No suitable color found for position ${currentPos}. Adjacent constraints conflict.`);
    }

    const color = availableColors[Math.floor(Math.random() * availableColors.length)];
    const colorType = ColorGenerator.getColorHueRange(color);
    assignment[currentPos] = { bgColor: color, borderColor: colorType };
  }

  const modelKeys = [...new Set(pairs.map(item => item.modelKey))];
  const sortedFiles = [...files].sort((a, b) => {
    const indexA = modelKeys.indexOf(a.modelKey);
    const indexB = modelKeys.indexOf(b.modelKey);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
  return { colors: assignment, sortedFiles: sortedFiles };
}

export {
  getDisplayModelInfo,
  getNetText,
  getFileParse,
  getPortListAndFileList,
  getDisplayCompName,
  getFilesParseList,
  getNextPairInfo,
  getDisplayModelFiles,
  getPortsObjAndSubcktList,
  getTargetModelFiles
}